import React, { useEffect, useState } from 'react';
import LoretteLogo from '../../images/loretteLogo.png';
import { login } from '../../api/apiCalls';
import { LoadingSpinner } from '../spinners';
import useUserToken from '../../context/userTokenContext';

const LoginCard = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorLog, setErrorLog] = useState(null); // Current error message
    const [errorTimeout, setErrorTimeout] = useState(null); // Timeout reference
    const setUserToken = useUserToken((state) => state.setUserToken);

    useEffect(() => {
        if (localStorage.getItem('token')) {
            console.log('User already logged in');
            window.location.href = '/dashboard';
        }
    }, []);

    const showErrorPopup = (message) => {
        setErrorLog(message); // Update with the latest error message

        if (errorTimeout) {
            clearTimeout(errorTimeout); // Clear existing timeout if active
        }

        const timeout = setTimeout(() => {
            setErrorLog(null); // Clear the error after the timeout
            setErrorTimeout(null); // Reset the timeout reference
        }, 3000); // Popup duration in milliseconds

        setErrorTimeout(timeout); // Store the new timeout reference
    };

    const loginUser = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            if (!username || !password) {
                showErrorPopup('Username and password are required');
                setIsLoading(false);
                return;
            }

            const response = await login(username, password);

            if (response.error) {
                // Log specific errors and provide user feedback
                if (response.status === 404) {
                    showErrorPopup('User not found');
                    setPassword('');
                } else {
                    showErrorPopup(`Login failed - ${response.error}`);
                }
            } else if (response.status === 200) {
                console.log('Login successful');
                setUserToken(response.data.token);
                localStorage.setItem('token', response.data.token);
                window.location.href = '/dashboard';
            }
        } catch (error) {
            showErrorPopup(`Unexpected error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mx-auto flex justify-center items-center min-h-screen">
            <div className="max-w-3xl w-full">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden flex flex-col md:flex-row justify-center items-center">
                    {/* Logo */}
                    <div className="w-[30%] flex justify-center items-center py-10 md:border-r-2">
                        <img src={LoretteLogo} alt="logo" className="w-32" />
                    </div>
                    {/* Form */}
                    <form onSubmit={loginUser} className="p-8 w-full">
                        <div className="text-center mb-6">
                            <h1 className="text-2xl font-bold text-gray-800">
                                Welcome to Network Management System!
                            </h1>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <input
                                    type="text"
                                    id="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full px-4 py-2 border bg-white border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter Username..."
                                />
                            </div>
                            <div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-4 py-2 border bg-white border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Password"
                                />
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="showPassword"
                                    checked={showPassword}
                                    onChange={() => setShowPassword(!showPassword)}
                                    className="form-checkbox"
                                />
                                <label htmlFor="showPassword" className="ml-2 text-sm text-gray-600">
                                    Show Password
                                </label>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400"
                            >
                                {isLoading ? <LoadingSpinner /> : 'Login'}
                            </button>
                        </div>
                    </form>
                </div>
                {/* Error Popup */}
                {errorLog && (
                    <div className="bg-red-500 text-white px-4 py-2 rounded shadow-md z-50">
                        {errorLog}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginCard;
